/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

type UnitDimension = 'energy' | 'mass' | 'power' | 'volume';

interface UnitDefinition {
    basicFactor: number;
    basicUnit: string;
    dimension: UnitDimension;
}

const UNIT_DEFINITIONS: Readonly<Record<string, UnitDefinition>> = {
    mWh: { basicFactor: 0.001, basicUnit: 'Wh', dimension: 'energy' },
    Wh: { basicFactor: 1, basicUnit: 'Wh', dimension: 'energy' },
    kWh: { basicFactor: 1_000, basicUnit: 'Wh', dimension: 'energy' },
    MWh: { basicFactor: 1_000_000, basicUnit: 'Wh', dimension: 'energy' },
    mW: { basicFactor: 0.001, basicUnit: 'W', dimension: 'power' },
    W: { basicFactor: 1, basicUnit: 'W', dimension: 'power' },
    kW: { basicFactor: 1_000, basicUnit: 'W', dimension: 'power' },
    MW: { basicFactor: 1_000_000, basicUnit: 'W', dimension: 'power' },
    'dm³': { basicFactor: 0.001, basicUnit: 'm³', dimension: 'volume' },
    'm³': { basicFactor: 1, basicUnit: 'm³', dimension: 'volume' },
    'dam³': { basicFactor: 1_000, basicUnit: 'm³', dimension: 'volume' },
    g: { basicFactor: 1, basicUnit: 'g', dimension: 'mass' },
    kg: { basicFactor: 1_000, basicUnit: 'g', dimension: 'mass' },
    t: { basicFactor: 1_000_000, basicUnit: 'g', dimension: 'mass' },
};

/**
 * Converts values between explicitly supported compatible units.
 */
export class UnitConverter {
    constructor(
        private readonly sourceUnit: string,
        private readonly targetUnit: string,
    ) {}

    public getFactor(): number | null {
        const definitions = this.getCompatibleDefinitions();

        if (!definitions) {
            return null;
        }

        return definitions.source.basicFactor / definitions.target.basicFactor;
    }

    public getBasicUnit(): string | null {
        return this.getCompatibleDefinitions()?.source.basicUnit ?? null;
    }

    public getBasicFactor(): number | null {
        return this.getCompatibleDefinitions()?.source.basicFactor ?? null;
    }

    private getCompatibleDefinitions(): { source: UnitDefinition; target: UnitDefinition } | null {
        const source = UNIT_DEFINITIONS[this.sourceUnit];
        const target = UNIT_DEFINITIONS[this.targetUnit];

        if (!source || !target || source.dimension !== target.dimension) {
            return null;
        }

        return { source, target };
    }
}
